import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { useModels } from "@/hooks/useModels";
import { useLanguage } from "@/contexts/LanguageContext";
import { SimpleTooltip } from "@/components/flowly/infra/SimpleTooltip/SimpleTooltip";
import { BarControl } from "./BarControl";
import { ModelSelectorProviderList } from "./ModelSelectorProviderList";
import { useModelSelector } from "./useModelSelector";
import type { MediaType } from "./types";

interface ModelSelectorControlProps {
  selectedModelId: string;
  mediaType: MediaType;
  onModelChange: (id: string) => void;
  models?: ReturnType<typeof useModels>["models"];
  getModelCost?: (modelId: string) => number;
  costsLoading?: boolean;
  singleModelTooltip?: string;
}

export function ModelSelectorControl({
  selectedModelId,
  mediaType,
  onModelChange,
  models: modelsProp,
  getModelCost,
  costsLoading,
  singleModelTooltip,
}: ModelSelectorControlProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const { models: fetchedModels } = useModels(mediaType);
  const models = modelsProp ?? fetchedModels;
  const { t } = useLanguage();
  const { providerGroups, expandedProvider, toggleProvider, expandProviderForModel, collapseAll } =
    useModelSelector(models);

  const selectedModel = models.find((m) => m.id === selectedModelId);
  const displayName = selectedModel?.displayName ?? selectedModelId;

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      expandProviderForModel(selectedModelId);
    } else {
      collapseAll();
    }
  };

  const handleSelect = (id: string) => {
    onModelChange(id);
    setOpen(false);
    collapseAll();
  };

  const triggerButton = (
    <BarControl>
      <span className="text-white font-extrabold text-xs whitespace-nowrap">{displayName}</span>
      {selectedModel?.badge === "recommended" && (
        <span className="px-1.5 py-0.5 rounded-full bg-amber-400/90 text-amber-950 text-[10px] font-bold leading-none">
          {t.generate.recommended}
        </span>
      )}
      {selectedModel?.badge === "new" && (
        <span className="px-1.5 py-0.5 rounded-full bg-blue-500/90 text-white text-[10px] font-bold leading-none">
          {t.generate.new}
        </span>
      )}
      <ChevronDown className="w-3 h-3 text-white/60" />
    </BarControl>
  );

  const listContent = (
    <ModelSelectorProviderList
      providerGroups={providerGroups}
      selectedModelId={selectedModelId}
      expandedProvider={expandedProvider}
      onProviderClick={toggleProvider}
      onModelSelect={handleSelect}
      mediaType={mediaType}
      getModelCost={getModelCost}
      costsLoading={costsLoading}
    />
  );

  if (models.length === 0) {
    return (
      <BarControl>
        <span className="text-white/40 text-xs">{t.generate.noModelsAvailable}</span>
      </BarControl>
    );
  }

  if (models.length === 1) {
    return (
      <SimpleTooltip content={singleModelTooltip} side="top">
        <div>
          <BarControl>
            <span className="text-white font-extrabold text-xs whitespace-nowrap">{displayName}</span>
            {selectedModel?.badge === "recommended" && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-400/90 text-amber-950 text-[10px] font-bold leading-none">
                {t.generate.recommended}
              </span>
            )}
          </BarControl>
        </div>
      </SimpleTooltip>
    );
  }

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{t.generate.selectProvider}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6">
            {listContent}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent
        side="top"
        sideOffset={8}
        className="w-96 p-2"
      >
        <p className="text-xs text-muted-foreground font-medium px-3 pb-1.5">
          {t.generate.selectProvider}
        </p>
        {listContent}
      </PopoverContent>
    </Popover>
  );
}
